import { useApolloClient, useMutation } from '@apollo/client';
import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Fragments, Mutations, Queries } from '@shared/api';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { toComponentOneOf } from '@shared/util/workflow';

type requiredWorkflowTemplateProperties =
  | 'components'
  | 'archivedComponents'
  | 'name'
  | 'requiresName'
  | 'behavior'
  | 'channelDefaultConfiguration';

export type WorkflowTemplateUpdateData = Pick<
  API.WorkflowTemplateFragment,
  'id' | requiredWorkflowTemplateProperties
>;

interface UpdateWorkflowTemplateInput {
  channelId?: number;
}

export function useUpdateWorkflowTemplate(params?: UpdateWorkflowTemplateInput) {
  const apolloClient = useApolloClient();
  const [updateWorkflow, { loading, error }] = useMutation<
    API.UpdateWorkflowTemplateMutation,
    API.UpdateWorkflowTemplateMutationVariables
  >(Mutations.workflow.updateWorkflow);
  const refetchQueries = useMemo(() => {
    return isNil(params?.channelId)
      ? []
      : [
          {
            query: Queries.card.listCardFieldStringTags,
            variables: { input: { channelId: params.channelId } },
          },
        ];
  }, [params?.channelId]);

  return [
    useCallback(
      async (workflowTemplate: WorkflowTemplateUpdateData) => {
        const cacheId = `WorkflowTemplate${workflowTemplate.id}`;
        const cachedData = apolloClient.readFragment<API.WorkflowTemplateFragment>({
          id: cacheId,
          fragment: Fragments.workflow.WorkflowTemplate,
          fragmentName: 'WorkflowTemplate',
        });
        return updateWorkflow({
          variables: {
            input: {
              id: workflowTemplate.id,
              name: workflowTemplate.name,
              requiresName: workflowTemplate.requiresName,
              components: workflowTemplate.components.map(toComponentOneOf).filter(notNil),
              archivedComponents: workflowTemplate.archivedComponents
                ?.map(toComponentOneOf)
                .filter(notNil),
            },
          },
          optimisticResponse: {
            updateWorkflowTemplate: {
              ...cachedData,
              ...workflowTemplate,
            } as API.WorkflowTemplateFragment,
          },
          refetchQueries,
          awaitRefetchQueries: true,
        });
      },
      [updateWorkflow, refetchQueries, apolloClient],
    ),
    { loading, error },
  ] as const;
}
